import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState, useEffect, useRef, useContext } from "react";
import { Modal, Row, Button } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "../../styles/StyleModals.css";
import { AuthContext } from "../../context/AuthContext";

// Function to validate image aspect ratio (height:width = 3:2) and file size
const validateImageRatio = (file) => {
  return new Promise((resolve) => {
    // Check file size first (2MB = 2 * 1024 * 1024 bytes)
    const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);

    if (file.size > maxSizeInBytes) {
      resolve({
        isValid: false,
        error: `File size too large (${fileSizeInMB}MB). Maximum allowed: 2MB`,
        fileName: file.name,
        fileSize: fileSizeInMB,
      });
      return;
    }

    const img = new Image();
    img.onload = () => {
      const actualRatio = img.width / img.height; // width:height ratio
      const expectedRatio = 2 / 3; // width:height = 2:3 = 0.667 (since height:width = 3:2)
      const tolerance = 0.1; // Allow 10% tolerance for rounding

      const isValidRatio = Math.abs(actualRatio - expectedRatio) <= tolerance;
      resolve({
        isValid: isValidRatio,
        actualRatio: actualRatio,
        expectedRatio: expectedRatio,
        width: img.width,
        height: img.height,
        fileName: file.name,
        fileSize: fileSizeInMB,
        heightToWidthRatio: img.height / img.width, // For display purposes
      });
    };
    img.onerror = () => {
      resolve({
        isValid: false,
        error: "Failed to load image",
        fileName: file.name,
        fileSize: fileSizeInMB,
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

// Helper function to create a crop area with aspect ratio 2:3 (width:height)
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

// Image Crop Modal Component - Canvas-based for WYSIWYG
const ImageCropModal = ({
  show,
  onHide,
  imageSrc,
  onCropComplete,
  fileName,
}) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [zoom, setZoom] = useState(1);
  const [displayImage, setDisplayImage] = useState(null);
  const displayCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const containerRef = useRef(null);

  const aspect = 2 / 3; // width:height = 2:3 (for height:width = 3:2)

  // Load original image
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => setDisplayImage(img);
    img.src = imageSrc;
  }, [imageSrc]);

  // Redraw display canvas whenever zoom changes
  useEffect(() => {
    if (!displayImage || !displayCanvasRef.current) return;

    const canvas = displayCanvasRef.current;
    const ctx = canvas.getContext("2d");

    // Calculate fixed canvas dimensions (fits in viewport)
    const maxWidth = 90 * window.innerWidth / 100;
    const maxHeight = 70 * window.innerHeight / 100;

    let displayWidth = displayImage.width;
    let displayHeight = displayImage.height;

    // Scale to fit viewport
    const scale = Math.min(maxWidth / displayWidth, maxHeight / displayHeight);
    displayWidth *= scale;
    displayHeight *= scale;

    // Set fixed canvas size - MUST match display dimensions for ReactCrop alignment
    const canvasWidth = Math.round(displayWidth);
    const canvasHeight = Math.round(displayHeight);
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Also set CSS size to match pixel size exactly
    canvas.style.width = canvasWidth + 'px';
    canvas.style.height = canvasHeight + 'px';

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Draw image scaled by zoom factor, centered on canvas
    const zoomedWidth = canvasWidth * zoom;
    const zoomedHeight = canvasHeight * zoom;
    const offsetX = (canvasWidth - zoomedWidth) / 2;
    const offsetY = (canvasHeight - zoomedHeight) / 2;

    ctx.drawImage(displayImage, offsetX, offsetY, zoomedWidth, zoomedHeight);

    // Initialize crop on first load
    if (!crop) {
      setCrop(centerAspectCrop(canvasWidth, canvasHeight, aspect));
    }
  }, [displayImage, zoom, crop]);

  const handleCropConfirm = async () => {
    if (!completedCrop || !displayImage || !displayCanvasRef.current || !previewCanvasRef.current) return;

    const displayCanvas = displayCanvasRef.current;
    const outputCanvas = previewCanvasRef.current;

    // Get display canvas dimensions
    const canvasWidth = displayCanvas.width;
    const canvasHeight = displayCanvas.height;

    // Get crop coordinates in display canvas space (percentage to pixels)
    const isPercent = completedCrop.unit === "%" || completedCrop.unit == null;
    const cropX = isPercent ? (completedCrop.x * canvasWidth) / 100 : completedCrop.x;
    const cropY = isPercent ? (completedCrop.y * canvasHeight) / 100 : completedCrop.y;
    const cropWidth = isPercent ? (completedCrop.width * canvasWidth) / 100 : completedCrop.width;
    const cropHeight = isPercent ? (completedCrop.height * canvasHeight) / 100 : completedCrop.height;

    // Calculate zoom offsets (from the drawing logic)
    const zoomedWidth = canvasWidth * zoom;
    const zoomedHeight = canvasHeight * zoom;
    const offsetX = (canvasWidth - zoomedWidth) / 2;
    const offsetY = (canvasHeight - zoomedHeight) / 2;

    // Map crop coordinates back to original image coordinates
    // Account for: zoom offset, zoom scale, and viewport scaling
    const origWidth = displayImage.width;
    const origHeight = displayImage.height;

    const cropX_unzoomed = (cropX - offsetX) / zoom;
    const cropY_unzoomed = (cropY - offsetY) / zoom;
    const cropWidth_unzoomed = cropWidth / zoom;
    const cropHeight_unzoomed = cropHeight / zoom;

    // Map from canvas (scaled) coordinates to original image coordinates
    const cropX_orig = (cropX_unzoomed * origWidth) / canvasWidth;
    const cropY_orig = (cropY_unzoomed * origHeight) / canvasHeight;
    const cropWidth_orig = (cropWidth_unzoomed * origWidth) / canvasWidth;
    const cropHeight_orig = (cropHeight_unzoomed * origHeight) / canvasHeight;

    // Set output canvas size - use the unzoomed, unscaled dimensions
    outputCanvas.width = Math.round(cropWidth_orig);
    outputCanvas.height = Math.round(cropHeight_orig);

    const outCtx = outputCanvas.getContext("2d");
    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = "high";

    // Extract directly from the original image at calculated coordinates
    outCtx.drawImage(
      displayImage,
      Math.round(cropX_orig),
      Math.round(cropY_orig),
      Math.round(cropWidth_orig),
      Math.round(cropHeight_orig),
      0,
      0,
      Math.round(cropWidth_orig),
      Math.round(cropHeight_orig)
    );

    // Export to file
    outputCanvas.toBlob(
      (blob) => {
        if (!blob) {
          const dataUrl = outputCanvas.toDataURL("image/jpeg", 0.95);
          const byteString = atob(dataUrl.split(",")[1]);
          const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++)
            ia[i] = byteString.charCodeAt(i);
          blob = new Blob([ab], { type: mimeString });
        }

        const croppedFile = new File([blob], fileName || "cropped_image.jpg", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        onCropComplete(croppedFile);
      },
      "image/jpeg",
      0.95
    );
  };



  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="crop-modal-dialog"
      contentClassName="crop-modal-content"
    >
      <Modal.Header closeButton>
        <Modal.Title>Crop Image to 3:2 Ratio (Height:Width)</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflow: "hidden", padding: "1rem" }}>
        <div
          style={{
            maxHeight: "80vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {displayImage && (
            <div
              ref={containerRef}
              style={{ position: "relative", width: "fit-content" }}
            >
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(percentCrop) => setCompletedCrop(percentCrop)}
                aspect={aspect}
              >
                <canvas
                  ref={displayCanvasRef}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: "90vw",
                    maxHeight: "60vh",
                    border: "1px solid #ccc",
                  }}
                />
              </ReactCrop>
            </div>
          )}

          {/* Zoom slider */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", maxWidth: "300px" }}>
            <label htmlFor="zoom-slider" style={{ minWidth: "60px" }}>Zoom:</label>
            <input
              id="zoom-slider"
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ flex: 1 }}
            />
            <span style={{ minWidth: "40px" }}>{(zoom * 100).toFixed(0)}%</span>
          </div>
        </div>
        <canvas ref={previewCanvasRef} style={{ display: "none" }} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleCropConfirm}>
          Apply Crop
        </Button>
      </Modal.Footer>
      <style jsx="true">{`
        .crop-modal-dialog {
          max-width: fit-content !important;
          width: auto !important;
          margin: 1.75rem auto;
        }
        .crop-modal-content {
          width: fit-content !important;
        }
      `}</style>
    </Modal>
  );
};

export const StyleFormModal = ({
  show,
  onHide,
  editingStyle,
  styleForm,
  setStyleForm,
  selectedMainCategory,
  handleMainCategoryChange,
  mainCategories,
  selectedMarketplaceCategory,
  handleMarketplaceCategoryChange,
  marketplaceCategories,
  marketplaceSubcategories,
  getMarketplaceSubcategoriesForCategory,
  getSubcategoriesForMainCategory,
  handleSaveStyle,
  loading,
  BASE_URL,
}) => {
  const [imageValidationError, setImageValidationError] = useState("");
  const [isValidatingImages, setIsValidatingImages] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentCropImage, setCurrentCropImage] = useState(null);
  const [currentCropIndex, setCurrentCropIndex] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [invalidImages, setInvalidImages] = useState([]);
  const [isLoadingProductNumber, setIsLoadingProductNumber] = useState(false);
  const [productNumberError, setProductNumberError] = useState("");
  const [generatedProductNumber, setGeneratedProductNumber] = useState(null);
  const [manualProductNumberEntry, setManualProductNumberEntry] = useState(false); // Track if manual entry is enabled
  const { userData } = useContext(AuthContext);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Clean up URLs when modal closes
  useEffect(() => {
    if (!show) {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls([]);
      setImageValidationError("");
      setIsValidatingImages(false);
      setPendingFiles([]);
      setProcessedFiles([]);
      setInvalidImages([]);
      setProductNumberError("");
      setGeneratedProductNumber(null);
      setIsLoadingProductNumber(false);
      setManualProductNumberEntry(false);
    }
  }, [show]);

  // Fetch code generation settings and auto-generate product number
  useEffect(() => {
    if (!show || editingStyle) {
      // Don't auto-generate when editing or modal is closed
      return;
    }

    const fetchProductNumberSettings = async () => {
      // Validate userData is available
      if (!userData || !userData.company_code) {
        console.warn("User data not available", userData);
        setProductNumberError(
          "Unable to load user information. Please refresh and try again."
        );
        setIsLoadingProductNumber(false);
        return;
      }

      setIsLoadingProductNumber(true);
      setProductNumberError("");
      setGeneratedProductNumber(null);

      try {
        const token = localStorage.getItem("authToken");
        const BASE_URL_API = BASE_URL || "http://localhost:3000";

        // Fetch product_number settings with company_code as query parameter
        const settingsUrl = new URL(
          `${BASE_URL_API}/api/admin/code-generation-settings/product_number`
        );
        settingsUrl.searchParams.append("company_code", userData.company_code);

        const response = await fetch(settingsUrl, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.setting) {
            // Check if manual entry is enabled
            if (data.setting.manually_add === 1 || data.setting.manually_add === true) {
              // Manual entry mode 
              setManualProductNumberEntry(true);
              setProductNumberError(""); // Clear any errors
              setGeneratedProductNumber(null);
              setIsLoadingProductNumber(false);
              return;
            }

            // Auto-generate mode
            setManualProductNumberEntry(false);
            try {
              const requestBody = {
                code_type: "product_number",
                company_code: userData.company_code, 
              };

              const generateResponse = await fetch(
                `${BASE_URL_API}/api/admin/code-generation-settings/preview-code`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(requestBody),
                }
              );

              if (generateResponse.ok) {
                const generateData = await generateResponse.json();
                if (generateData.success && generateData.code) {
                  setGeneratedProductNumber(generateData.code);
                  // Auto-fill the product number field
                  setStyleForm((prev) => ({
                    ...prev,
                    style_number: generateData.code,
                  }));
                } else {
                  console.error("Generation failed:", generateData.message);
                  setProductNumberError(
                    generateData.message || "Failed to generate product number"
                  );
                }
              } else {
                const errorData = await generateResponse.json().catch(() => ({}));
                console.error("Generate response error:", errorData);
                setProductNumberError(
                  "Failed to generate product number. Please enter manually."
                );
              }
            } catch (genError) {
              console.error("Error generating code:", genError);
              setProductNumberError(
                "Unable to generate product number. Please enter manually."
              );
            }
          } else {
            // Settings don't exist
            setProductNumberError(
              "Product Number settings not configured. Please configure code generation settings first, or enter the product number manually."
            );
          }
        } else if (response.status === 404) {
          // Settings not found
          setProductNumberError(
            "Product Number settings not configured. Please configure code generation settings first, or enter the product number manually."
          );
        } else {
          setProductNumberError("Unable to fetch product number settings.");
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        setProductNumberError(
          "Error checking product number settings. Please enter manually."
        );
      } finally {
        setIsLoadingProductNumber(false);
      }
    };

    fetchProductNumberSettings();
  }, [show, editingStyle, BASE_URL, userData?.company_code]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      // Clean up previous URLs
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls([]);
      return;
    }

    setIsValidatingImages(true);
    setImageValidationError("");

    // Clean up previous URLs
    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    try {
      const validationResults = await Promise.all(
        files.map((file) => validateImageRatio(file))
      );

      const invalidImgs = validationResults.filter((result) => !result.isValid);
      const validFiles = files.filter(
        (_, idx) => validationResults[idx].isValid
      );

      if (invalidImgs.length > 0) {
        // Store invalid images info for cropping
        const invalidImagesList = invalidImgs.map((result, idx) => ({
          file: files[validationResults.indexOf(result)],
          result: result,
        }));

        setInvalidImages(invalidImagesList);
        setPendingFiles(files);
        setProcessedFiles(validFiles);

        // If there are valid files, show them
        if (validFiles.length > 0) {
          const validPreviewUrls = validFiles.map((file) =>
            URL.createObjectURL(file)
          );
          setPreviewUrls(validPreviewUrls);
        }

        // Automatically open crop modal for the first invalid image
        const firstInvalid = invalidImagesList[0];
        if (firstInvalid && firstInvalid.file) {
          const imageUrl = URL.createObjectURL(firstInvalid.file);
          setCurrentCropImage(imageUrl);
          setCurrentCropIndex(0);
          setShowCropModal(true);
        }
      } else {
        // All images are valid
        const newPreviewUrls = files.map((file) => URL.createObjectURL(file));
        setPreviewUrls(newPreviewUrls);
        setProcessedFiles(files);
        // setInvalidImages([]);

        setStyleForm({
          ...styleForm,
          images: files,
        });
      }
    } catch (error) {
      setImageValidationError("Error validating images. Please try again.");
      e.target.value = "";
      setPreviewUrls([]);
    }

    setIsValidatingImages(false);
  };

  const handleCropComplete = (croppedFile) => {
    // Replace the invalid file with cropped file
    const imageInfo = invalidImages[currentCropIndex];
    const originalFileIndex = pendingFiles.indexOf(imageInfo.file);

    const newPendingFiles = [...pendingFiles];
    newPendingFiles[originalFileIndex] = croppedFile;

    // Add to processed files
    const newProcessedFiles = [...processedFiles, croppedFile];
    setProcessedFiles(newProcessedFiles);

    // Remove from invalid images
    const newInvalidImages = invalidImages.filter(
      (_, idx) => idx !== currentCropIndex
    );
    setInvalidImages(newInvalidImages);

    // Update preview URLs
    const newPreviewUrl = URL.createObjectURL(croppedFile);
    setPreviewUrls([...previewUrls, newPreviewUrl]);

    // Clean up current crop image
    URL.revokeObjectURL(currentCropImage);
    setShowCropModal(false);
    setCurrentCropImage(null);
    setCurrentCropIndex(null);

    // Update form if all images are processed
    if (newInvalidImages.length === 0) {
      setStyleForm({
        ...styleForm,
        images: newProcessedFiles,
      });
      setImageValidationError("");
    } else {
      // Automatically open crop modal for the next invalid image
      const nextInvalid = newInvalidImages[0];
      if (nextInvalid && nextInvalid.file) {
        const imageUrl = URL.createObjectURL(nextInvalid.file);
        setCurrentCropImage(imageUrl);
        setCurrentCropIndex(0);
        setShowCropModal(true);
      }
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="large">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingStyle ? "Edit Product" : "Add New Product"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <div className="form-group col-md-6">
              <label className="form-label" htmlFor="style-number">
                Product Number *
              </label>
              {isLoadingProductNumber && (
                <div style={{ color: "#0066cc", fontSize: "0.7rem", marginBottom: "0.1rem" }}>
                  Generating product number...
                </div>
              )}
              {manualProductNumberEntry && !isLoadingProductNumber && (
                <div style={{ color: "#6b7280", fontSize: "0.7rem", marginBottom: "0.1rem" }}>
                  Please enter a product number
                </div>
              )}
              {generatedProductNumber && !productNumberError && !manualProductNumberEntry && (
                <div style={{ color: "#009900", fontSize: "0.7rem", marginBottom: "0.1rem" }}>
                  Auto-generated product number
                </div>
              )}
              {productNumberError && (
                <div style={{ color: "#cc3300", fontSize: "0.7rem", marginBottom: "0.1rem" }}>
                  {productNumberError}
                </div>
              )}
              <input
                id="style-number"
                name="style_number"
                type="text"
                className="form-input"
                value={styleForm.style_number}
                onChange={(e) =>
                  setStyleForm({ ...styleForm, style_number: e.target.value })
                }
                placeholder={isLoadingProductNumber ? "Generating..." : manualProductNumberEntry ? "Enter product number" : "Auto-generated product number"}
                required
                disabled={isLoadingProductNumber}
                readOnly={generatedProductNumber && !manualProductNumberEntry && !productNumberError}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="form-label" htmlFor="style-name">
                Name *
              </label>
              <input
                id="style-name"
                name="name"
                type="text"
                className="form-input"
                value={styleForm.name}
                onChange={(e) =>
                  setStyleForm({ ...styleForm, name: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group col-md-12">
              <label className="form-label" htmlFor="style-description">
                Description *
              </label>
              <textarea
                id="style-description"
                name="description"
                className="form-textarea"
                value={styleForm.description}
                onChange={(e) =>
                  setStyleForm({ ...styleForm, description: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group col-md-6">
              <label className="form-label" htmlFor="main-category">
                Main Category *
              </label>
              <select
                id="main-category"
                name="main_category_id"
                className="form-select"
                value={styleForm.main_category_id ? styleForm.main_category_id.toString() : ""}
                onChange={(e) => handleMainCategoryChange(e.target.value)}
              >
                <option value="">Select Main Category</option>
                {mainCategories.map((category) => (
                  <option
                    key={category.category_id}
                    value={category.category_id}
                  >
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedMainCategory ||
              (editingStyle && editingStyle.main_category_id)) && (
                <div className="form-group col-md-6">
                  <label className="form-label" htmlFor="sub-category">
                    Sub Category *
                  </label>
                  <select
                    id="sub-category"
                    name="category_id"
                    className="form-select"
                    value={styleForm.category_id ? styleForm.category_id.toString() : ""}
                    onChange={(e) =>
                      setStyleForm({ ...styleForm, category_id: e.target.value })
                    }
                  >
                    <option value="">Select Sub Category</option>
                    {getSubcategoriesForMainCategory().map((subCategory) => (
                      <option
                        key={subCategory.category_id}
                        value={subCategory.category_id}
                      >
                        {subCategory.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="form-group col-md-6">
              <label className="form-label" htmlFor="marketplace-category">
                Marketplace Category *
              </label>
              <select
                id="marketplace-category"
                name="marketplace_main_category_id"
                className="form-select"
                value={styleForm.marketplace_main_category_id ? styleForm.marketplace_main_category_id.toString() : ""}
                onChange={(e) =>
                  handleMarketplaceCategoryChange(e.target.value)
                }
              >
                <option value="">Select Marketplace Category</option>
                {marketplaceCategories.map((category) => (
                  <option
                    key={category.marketplace_category_id}
                    value={category.marketplace_category_id}
                  >
                    {category.marketplace_category_name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedMarketplaceCategory ||
              (editingStyle && editingStyle.marketplace_category_id)) && (
                <div className="form-group col-md-6">
                  <label
                    className="form-label"
                    htmlFor="marketplace-sub-category"
                  >
                    Marketplace Sub Category *
                  </label>
                  <select
                    id="marketplace-sub-category"
                    name="marketplace_category_id"
                    className="form-select"
                    value={styleForm.marketplace_category_id ? styleForm.marketplace_category_id.toString() : ""}
                    onChange={(e) =>
                      setStyleForm({
                        ...styleForm,
                        marketplace_category_id: e.target.value
                      })
                    }
                  >
                    <option value="">Select Marketplace Sub Category</option>
                    {getMarketplaceSubcategoriesForCategory(styleForm.marketplace_main_category_id).map((subCategory) => (
                      <option
                        key={subCategory.marketplace_category_id}
                        value={subCategory.marketplace_category_id}
                      >
                        {subCategory.marketplace_category_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="form-group col-md-12">
              <label className="form-label" htmlFor="style-images">
                Images
              </label>
              <div className="image-upload-note">
                <small className="text-muted">
                  📸 Please upload images with height:width ratio of 3:2 (e.g.,
                  300x200px, 600x400px) and maximum file size of 2MB
                </small>
              </div>
              <input
                id="style-images"
                name="images"
                type="file"
                className="form-input"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                disabled={isValidatingImages}
              />
              {isValidatingImages && (
                <div className="validation-message validating">
                  Validating image aspect ratios...
                </div>
              )}
              {imageValidationError && (
                <div className="validation-message error">
                  {imageValidationError}
                </div>
              )}
              {!imageValidationError &&
                !isValidatingImages &&
                processedFiles.length > 0 && (
                  <div className="validation-message success">
                    ✓ Image Uploaded Successfully.
                  </div>
                )}

              {/* Preview of newly selected images */}
              {processedFiles.length > 0 && previewUrls.length > 0 && (
                <div className="new-images-preview">
                  <p className="preview-label">Selected Images:</p>
                  <div className="image-previews">
                    {processedFiles.map((file, idx) => (
                      <div key={idx} className="image-preview-item">
                        <img
                          src={previewUrls[idx]}
                          alt={`Preview ${idx + 1}`}
                          className="preview-thumbnail"
                        />
                        <span className="image-name">{file.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editingStyle && editingStyle.image && (
                <div className="existing-images">
                  <p className="existing-label">Existing Images:</p>
                  <div className="image-previews">
                    {editingStyle.image.split(",").map((img, idx) => (
                      <div key={idx} className="image-preview-item">
                        <img
                          src={`${BASE_URL}/uploads/styles/${img.trim()}`}
                          alt={`Existing ${idx + 1}`}
                          className="existing-thumbnail"
                        />
                        <span className="image-name">{img.trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button className="cancel-btn" onClick={onHide}>
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSaveStyle}
            disabled={loading || !styleForm.name || !styleForm.category_id}
          >
            {loading
              ? "Saving..."
              : editingStyle
                ? "Update Product"
                : "Add Product"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Image Crop Modal */}
      <ImageCropModal
        show={showCropModal}
        onHide={() => {
          setShowCropModal(false);
          if (currentCropImage) {
            URL.revokeObjectURL(currentCropImage);
          }
          setCurrentCropImage(null);
          setCurrentCropIndex(null);
        }}
        imageSrc={currentCropImage}
        onCropComplete={handleCropComplete}
        fileName={
          invalidImages[currentCropIndex]?.result?.fileName ||
          "cropped_image.jpg"
        }
      />
    </>
  );
};

export const VariantFormModal = ({
  show,
  onHide,
  selectedStyle,
  setVariantForm,
  handleDeleteVariant,
  variants,
  colors,
  sizes,
  materials,
  fits,
  setIsEditing,
  setEditingId,
  companyCurrency,
}) => {
  // Use companyCurrency for table headings
  const currency = companyCurrency || "USD";
  const columns = useMemo(
    () => {
      // Determine which columns should be shown based on available data
      const showColor = (Array.isArray(colors) && colors.length > 0) || (Array.isArray(variants) && variants.some(v => v.color_id));
      const showSize = (Array.isArray(sizes) && sizes.length > 0) || (Array.isArray(variants) && variants.some(v => v.size_id));
      const showFit = (Array.isArray(fits) && fits.length > 0) || (Array.isArray(variants) && variants.some(v => v.fit_id));
      const showMaterial = (Array.isArray(materials) && materials.length > 0) || (Array.isArray(variants) && variants.some(v => v.material_id));

      const cols = [];
      cols.push({ header: "SKU", accessorKey: "sku" });

      if (showColor) {
        cols.push({
          header: "Color",
          accessorFn: (row) => colors.find((c) => c.color_id === row.color_id)?.color_name || "",
          cell: ({ row }) => {
            const color = colors.find((c) => c.color_id === row.original.color_id);
            return (
              <div className="d-flex align-items-center gap-2">
                <span
                  className="color-swatch"
                  style={{
                    backgroundColor: color?.color_code || "#fff",
                    border: "1px solid #dee2e6",
                  }}
                />
                <span>{color?.color_name || ""}</span>
              </div>
            );
          },
        });
      }

      if (showSize) {
        cols.push({ header: "Size", accessorFn: (row) => sizes.find((s) => s.size_id === row.size_id)?.size_name || "" });
      }

      if (showFit) {
        cols.push({ header: "Fit", accessorFn: (row) => fits.find((f) => f.fit_id === row.fit_id)?.fit_name || "" });
      }

      if (showMaterial) {
        cols.push({ header: "Material", accessorFn: (row) => materials.find((m) => m.material_id === row.material_id)?.material_name || "" });
      }

      cols.push({ header: `Unit price (${currency})`, accessorKey: "unit_price", cell: ({ getValue }) => (<div style={{ textAlign: "right" }}>{getValue()}</div>) });
      cols.push({ header: `Sale price (${currency})`, accessorKey: "sale_price", cell: ({ getValue }) => (<div style={{ textAlign: "right" }}>{getValue()}</div>) });

      return cols;
    },
    [colors, sizes, fits, materials, variants, currency]
  );

  const table = useReactTable({
    data: variants,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedStyle && `Variants for ${selectedStyle.style_number}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="variants-table-container">
          <div className="table-controls">
            <input
              id="variant-search"
              name="search"
              type="text"
              placeholder="Search variants..."
              className="search-input"
              onChange={(e) => table.setGlobalFilter(e.target.value)}
            />
            <select
              id="variant-page-size"
              name="pageSize"
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="page-size-select"
            >
              {[10, 25, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>

          <div className="table-responsive">
            <table className="styles-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        <span className="sort-indicator">
                          {header.column.getIsSorted()
                            ? header.column.getIsSorted() === "desc"
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {/* Column filter row - first row in the table body */}
                <tr className="filter-row">
                  {table.getHeaderGroups()[0].headers.map((header) => (
                    <td key={`filter-${header.id}`}>
                      {!header.isPlaceholder ? (
                        <input
                          id={`variant-filter-${header.id}`}
                          name={`filter_${header.id}`}
                          type="text"
                          className="column-filter-input"
                          placeholder={`Search ${String(
                            header.column.columnDef.header
                          )}`}
                          value={
                            typeof header.column.getFilterValue === "function"
                              ? header.column.getFilterValue() ?? ""
                              : ""
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (
                              typeof header.column.setFilterValue === "function"
                            ) {
                              header.column.setFilterValue(
                                e.target.value || undefined
                              );
                            } else if (
                              typeof header.column.setFilter === "function"
                            ) {
                              header.column.setFilter(
                                e.target.value || undefined
                              );
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : null}
                    </td>
                  ))}
                </tr>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </button>
            <span>
              Page{" "}
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
